<template>
  <v-container fluid class="group-details-container">
    <!-- Header Section - Fixed on mobile -->
    <v-card class="header-card elevation-2 mb-4 mb-md-6" :class="{ 'mobile-header': $vuetify.display.xs }">
      <v-card-title class="pa-4 pa-md-6">
        <v-row align="center" no-gutters>
          <!-- Back button and title -->
          <v-col cols="12" sm="auto" class="mb-3 mb-sm-0">
            <div class="d-flex align-center">
              <v-btn
                icon="mdi-arrow-left"
                variant="text"
                color="primary"
                @click="goBack"
                class="mr-2 mr-md-3"
                size="small"
              />
              <div>
                <div class="text-h5 text-md-h4 font-weight-bold text-primary">
                  Detalles del grupo
                </div>
                <div class="text-caption text-md-body-2 text-medium-emphasis mt-1" v-if="currentGroup">
                  {{ currentGroup.name }}
                </div>
              </div>
            </div>
          </v-col>

          <v-spacer class="d-none d-sm-flex"></v-spacer>

          <!-- Action buttons -->
          <v-col cols="12" sm="auto" v-if="currentGroup">
            <div class="d-flex flex-column flex-sm-row ga-2">
              <v-btn
                color="primary"
                variant="tonal"
                prepend-icon="mdi-pencil"
                @click="editGroup"
                class="text-none"
                :block="$vuetify.display.xs"
                :size="$vuetify.display.xs ? 'default' : 'default'"
              >
                Editar Grupo
              </v-btn>
              <v-btn
                :color="currentGroup.is_active ? 'warning' : 'success'"
                variant="tonal"
                :prepend-icon="currentGroup.is_active ? 'mdi-archive' : 'mdi-restore'"
                @click="toggleGroupStatus"
                class="text-none"
                :block="$vuetify.display.xs"
                :size="$vuetify.display.xs ? 'default' : 'default'"
              >
                {{ currentGroup.is_active ? 'Desactivar' : 'Restaurar' }}
              </v-btn>
            </div>
          </v-col>
        </v-row>
      </v-card-title>
    </v-card>

    <!-- Loading State -->
    <v-card v-if="loading" class="loading-card elevation-1">
      <v-card-text class="text-center pa-8 pa-md-12">
        <v-progress-circular indeterminate color="primary" size="64" width="6" />
        <div class="text-h6 mt-6 text-medium-emphasis">Cargando detalles del grupo...</div>
      </v-card-text>
    </v-card>

    <!-- Error State -->
    <v-alert
      v-else-if="error"
      type="error"
      variant="tonal"
      prominent
      border="start"
      class="mb-6 error-alert"
    >
      <v-alert-title class="text-h6 mb-2">Error cargando grupo</v-alert-title>
      <div class="mb-3">{{ error }}</div>
      <v-btn
        color="error"
        variant="elevated"
        prepend-icon="mdi-refresh"
        @click="fetchGroup(groupId)"
        class="text-none"
      >
        Reintentar
      </v-btn>
    </v-alert>

    <!-- Group Information -->
    <v-row v-else-if="currentGroup">
      <!-- Status Card - Highlighted -->
      <v-col cols="12" md="4">
        <v-card
          class="status-card elevation-3 h-100"
          :class="currentGroup.is_active ? 'active-group' : 'inactive-group'"
        >
          <v-card-text class="pa-6">
            <div class="d-flex align-center mb-4">
              <v-avatar
                :color="currentGroup.is_active ? 'success' : 'error'"
                size="56"
                class="mr-4"
              >
                <v-icon size="32" color="white">
                  {{ currentGroup.is_active ? 'mdi-check-circle' : 'mdi-cancel' }}
                </v-icon>
              </v-avatar>
              <div>
                <div class="text-overline text-medium-emphasis">Estado del Grupo</div>
                <div class="text-h5 font-weight-bold">
                  {{ currentGroup.is_active ? 'Activo' : 'Inactivo' }}
                </div>
              </div>
            </div>
            <v-chip
              :color="currentGroup.is_active ? 'success' : 'error'"
              variant="flat"
              size="small"
              label
              block
            >
              <v-icon start size="small">
                {{ currentGroup.is_active ? 'mdi-check' : 'mdi-close' }}
              </v-icon>
              {{ currentGroup.is_active ? 'Grupo Operativo' : 'Grupo Archivado' }}
            </v-chip>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Main Information Card -->
      <v-col cols="12" md="8">
        <v-card class="info-card elevation-3 h-100">
          <v-card-title class="d-flex align-center pa-4 pa-md-6 bg-primary-subtle">
            <v-icon color="primary" size="28" class="mr-3">mdi-information-outline</v-icon>
            <span class="text-h6 text-md-h5 font-weight-medium">Información General</span>
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text class="pa-4 pa-md-6">
            <!-- Group Name -->
            <div class="info-item mb-6">
              <div class="d-flex align-center mb-2">
                <v-icon color="primary" size="20" class="mr-2">mdi-tag</v-icon>
                <span class="text-overline text-medium-emphasis">Nombre del Grupo</span>
              </div>
              <div class="text-h6 text-md-h5 font-weight-medium ml-7">
                {{ currentGroup.name }}
              </div>
            </div>

            <!-- Description -->
            <div class="info-item mb-6">
              <div class="d-flex align-center mb-2">
                <v-icon color="primary" size="20" class="mr-2">mdi-text</v-icon>
                <span class="text-overline text-medium-emphasis">Descripción</span>
              </div>
              <div class="text-body-1 ml-7" style="line-height: 1.6;">
                {{ currentGroup.description || 'Sin descripción proporcionada' }}
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Dates Card -->
      <v-col cols="12">
        <v-card class="dates-card elevation-3">
          <v-card-title class="d-flex align-center pa-4 pa-md-6 bg-info-subtle">
            <v-icon color="info" size="28" class="mr-3">mdi-calendar-clock</v-icon>
            <span class="text-h6 text-md-h5 font-weight-medium">Fechas y Registro</span>
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text class="pa-4 pa-md-6">
            <v-row>
              <!-- Created At -->
              <v-col cols="12" sm="6">
                <v-card class="date-item-card elevation-0" color="blue-lighten-5">
                  <v-card-text class="pa-4">
                    <div class="d-flex align-center mb-3">
                      <v-avatar color="info" size="40" class="mr-3">
                        <v-icon size="24" color="white">mdi-calendar-plus</v-icon>
                      </v-avatar>
                      <div>
                        <div class="text-overline text-medium-emphasis">Fecha de Creación</div>
                        <div class="text-h6 font-weight-medium text-info">
                          {{ formatDate(currentGroup.created_at) }}
                        </div>
                      </div>
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>

              <!-- Updated At -->
              <v-col cols="12" sm="6" v-if="currentGroup.updated_at">
                <v-card class="date-item-card elevation-0" color="purple-lighten-5">
                  <v-card-text class="pa-4">
                    <div class="d-flex align-center mb-3">
                      <v-avatar color="purple" size="40" class="mr-3">
                        <v-icon size="24" color="white">mdi-calendar-edit</v-icon>
                      </v-avatar>
                      <div>
                        <div class="text-overline text-medium-emphasis">Última Modificación</div>
                        <div class="text-h6 font-weight-medium text-purple">
                          {{ formatDate(currentGroup.updated_at) }}
                        </div>
                      </div>
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Edit Form Dialog -->
    <GroupForm v-model="formDialog" :group="currentGroup" @saved="handleGroupSaved" />

    <!-- Confirm Dialog -->
    <ConfirmDialog
      v-model="confirmDialog.show"
      :title="confirmDialog.title"
      :text="confirmDialog.text"
      :color="confirmDialog.color"
      :loading="confirmDialog.loading"
      @confirmed="handleConfirmAction"
      @cancelled="handleCancelAction"
    />

    <!-- Snackbar -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="4000"
      location="top right"
    >
      {{ snackbar.text }}
      <template #actions>
        <v-btn variant="text" @click="snackbar.show = false"> Cerrar </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import GroupForm from './GroupForm.vue'
import ConfirmDialog from '../common/ConfirmDialog.vue'

export default {
  name: 'GroupDetails',
  components: {
    GroupForm,
    ConfirmDialog,
  },
  props: {
    id: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      pins: [],
      loadingPins: false,
      formDialog: false,
      confirmDialog: {
        show: false,
        title: '',
        text: '',
        color: '',
        loading: false,
        callback: null,
      },
      snackbar: {
        show: false,
        text: '',
        color: 'success',
      },
    }
  },
  computed: {
    ...mapState('groups', ['currentGroup', 'loading', 'error']),
    ...mapGetters('groups', ['getCurrentGroup', 'isLoading']),

    groupId() {
      return parseInt(this.id)
    },

    usedPinsCount() {
      return this.pins.filter((pin) => pin.is_used).length
    },

    unusedPinsCount() {
      return this.pins.filter((pin) => !pin.is_used).length
    },
  },
  async mounted() {
    await this.loadGroupDetails()
  },
  methods: {
    ...mapActions('groups', ['fetchGroup', 'deactivateGroup', 'restoreGroup', 'clearCurrentGroup']),

    async loadGroupDetails() {
      await this.fetchGroup(this.groupId)
    },

    editGroup() {
      this.formDialog = true
    },

    toggleGroupStatus() {
      if (this.currentGroup.is_active) {
        this.confirmDialog = {
          show: true,
          title: '¿Desactivar grupo?',
          text: `¿Estás seguro que deseas desactivar el grupo "${this.currentGroup.name}"? Este grupo dejará de estar disponible.`,
          color: 'warning',
          loading: false,
          callback: this.handleDeactivateGroup,
        }
      } else {
        this.confirmDialog = {
          show: true,
          title: '¿Restaurar grupo?',
          text: `¿Estás seguro que deseas restaurar el grupo "${this.currentGroup.name}"? Este grupo volverá a estar activo.`,
          color: 'success',
          loading: false,
          callback: this.handleRestoreGroup,
        }
      }
    },

    async handleConfirmAction() {
      if (this.confirmDialog.callback) {
        this.confirmDialog.loading = true
        await this.confirmDialog.callback()
        this.confirmDialog.loading = false
        this.confirmDialog.show = false
        this.confirmDialog.callback = null
      }
    },

    handleCancelAction() {
      this.confirmDialog.show = false
      this.confirmDialog.callback = null
    },

    async handleDeactivateGroup() {
      try {
        const result = await this.deactivateGroup(this.groupId)
        if (result.success) {
          this.showSnackbar('Grupo desactivado exitosamente', 'success')
        } else {
          this.showSnackbar(result.error || 'Error al desactivar grupo', 'error')
        }
      } catch (error) {
        this.showSnackbar('Error al desactivar grupo', 'error')
      }
    },

    async handleRestoreGroup() {
      try {
        const result = await this.restoreGroup(this.groupId)
        if (result.success) {
          this.showSnackbar('Grupo restaurado exitosamente', 'success')
        } else {
          this.showSnackbar(result.error || 'Error al restaurar grupo', 'error')
        }
      } catch (error) {
        this.showSnackbar('Error al restaurar grupo', 'error')
      }
    },

    async handleGroupSaved(message) {
      this.formDialog = false
      this.showSnackbar(message, 'success')
      await this.fetchGroup(this.groupId)
    },

    goBack() {
      this.$router.push({ name: 'Groups' })
    },

    showSnackbar(text, color = 'success') {
      this.snackbar = { show: true, text, color }
    },

    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
  },

  beforeUnmount() {
    this.clearCurrentGroup()
  },
}
</script>

<style scoped>
/* Container */
.group-details-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 16px;
}

@media (min-width: 960px) {
  .group-details-container {
    padding: 24px;
  }
}

/* Header Card */
.header-card {
  border-radius: 16px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border: 1px solid rgba(0, 0, 0, 0.05);
}

.header-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12) !important;
}

.mobile-header {
  position: sticky;
  top: 0;
  z-index: 10;
}

/* Status Card */
.status-card {
  border-radius: 16px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.status-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  transition: all 0.3s ease;
}

.active-group::before {
  background: linear-gradient(90deg, #4caf50, #66bb6a);
}

.inactive-group::before {
  background: linear-gradient(90deg, #f44336, #ef5350);
}

.status-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.15) !important;
}

/* Info Card */
.info-card {
  border-radius: 16px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.info-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12) !important;
}

.bg-primary-subtle {
  background-color: rgba(25, 118, 210, 0.08);
}

.info-item {
  transition: all 0.2s ease;
  padding: 8px;
  border-radius: 8px;
}

.info-item:hover {
  background-color: rgba(0, 0, 0, 0.02);
}

/* Dates Card */
.dates-card {
  border-radius: 16px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.dates-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12) !important;
}

.bg-info-subtle {
  background-color: rgba(33, 150, 243, 0.08);
}

.date-item-card {
  border-radius: 12px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid rgba(0, 0, 0, 0.05);
}

.date-item-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
}

/* Loading Card */
.loading-card {
  border-radius: 16px;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
}

/* Error Alert */
.error-alert {
  border-radius: 12px;
  animation: slideIn 0.3s ease-out;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Typography */
.text-primary {
  color: #1976d2 !important;
}

.text-info {
  color: #2196f3 !important;
}

.text-purple {
  color: #9c27b0 !important;
}

/* Buttons */
.v-btn {
  text-transform: none;
  transition: all 0.2s ease;
}

.v-btn:hover {
  transform: translateY(-1px);
}

/* Chips */
.v-chip {
  font-weight: 500;
  transition: all 0.2s ease;
}

/* Avatars */
.v-avatar {
  transition: all 0.3s ease;
}

.v-avatar:hover {
  transform: scale(1.05);
}

/* Responsive spacing */
@media (max-width: 599px) {
  .group-details-container {
    padding: 12px;
  }

  .header-card {
    border-radius: 12px;
  }

  .status-card,
  .info-card,
  .dates-card {
    border-radius: 12px;
    margin-bottom: 12px;
  }

  .date-item-card {
    margin-bottom: 12px;
  }
}

@media (min-width: 600px) and (max-width: 959px) {
  .group-details-container {
    padding: 16px;
  }
}

/* Animations */
.v-row > .v-col {
  animation: fadeInUp 0.5s ease-out;
  animation-fill-mode: both;
}

.v-row > .v-col:nth-child(1) {
  animation-delay: 0.1s;
}

.v-row > .v-col:nth-child(2) {
  animation-delay: 0.2s;
}

.v-row > .v-col:nth-child(3) {
  animation-delay: 0.3s;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Card hover effects */
.v-card {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Text readability */
.text-body-1 {
  line-height: 1.75;
}

.text-h6 {
  line-height: 1.4;
}

/* Spacing utilities */
.ga-2 {
  gap: 8px;
}

/* Height utilities */
.h-100 {
  height: 100%;
}

/* Custom scrollbar */
@media (min-width: 960px) {
  .group-details-container::-webkit-scrollbar {
    width: 8px;
  }

  .group-details-container::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }

  .group-details-container::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 4px;
  }

  .group-details-container::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
}
</style>
